import React from 'react';
import { useCallback } from 'react';
import meetingState from '../../../../../../../data/constants/meetingState';
import { useDateURLFormatter } from '../../../../../../../data/hooks/datetimeHooks';
import { useMeetings } from '../../../../../../../data/hooks/meeting/useMeetings';
import { useUnblockSlotCallback } from '../useUnblockSlotCallback';
import { dialogAlert, dialogConfirm, dialogMultipleOptions } from '../../../../../../elements/Dialog/dialogs';
import { useI18n } from '../../../../../../../data/hooks/i18nHooks';
import TimeInterval from '../../../../../../elements/TimeInterval/TimeInterval';
import { isSessionOverlaping } from 'common-data-handling';
import { useUserSchedule } from '../../../../../../../data/hooks/schedule/useUserSchedule';
import { isMeetingOverlaping } from '../../../../../../../data/filters/meetingFilters';

export function useSelectSlotCallback(event, setSlot) {
    const userMeetings = useMeetings(event.id, { filter: (m) => m.state != meetingState.CANCELLED }),
        eventSchedule = useUserSchedule(event.id),
        unblockSlot = useUnblockSlotCallback(event.id),
        formatURLDate = useDateURLFormatter(),
        i18n = useI18n();

    return useCallback(
        async (slot) => {
            // No slot selected
            if (!slot) return setSlot(null);

            // Slot has no locations available
            if (!slot.location_ids.length) {
                await dialogAlert(i18n['msg.unavailable-meetingpoint'], <TimeInterval event={event} slot={slot} />);
                return;
            }

            // Slot was blocked by user
            if (slot.self_blocked) {
                const unblock = await dialogConfirm(
                    <>
                        <p>{i18n['title.blocked-slot']}</p>
                        <p>{i18n['label.ask-to-unblock-slot']}</p>
                    </>,
                    <TimeInterval event={event} slot={slot} />,
                    i18n['label.unblock'],
                    i18n['navigation.back']
                );

                if (unblock) {
                    await unblockSlot(slot);
                    setSlot(slot);
                }
                return;
            }

            // User is busy with a meeting at the same time
            if (!slot.self_available || (slot.self_busy && slot.type == 'meeting')) {
                let title = i18n['title.unavailable-slot'],
                    message = i18n['message.session-slot'];
                if (slot.type == 'speeddate') {
                    title = i18n['title.speeddate-session-slot'];
                    message = '';
                } else if (slot.type == 'meeting') {
                    title = slot.self_busy ? i18n['title.meeting-slot-confirmed'] : i18n['title.meeting-slot-unconfirmed'];
                    message = i18n['message.meeting-slot'];
                }

                const goview = await dialogConfirm(
                    <>
                        <p>{title}</p>
                        {message ? <p>{message}</p> : null}
                    </>,
                    <TimeInterval event={event} slot={slot} />,
                    i18n['label.view-meetings'],
                    i18n['navigation.back']
                );

                if (goview) {
                    const blockMeetings = userMeetings.filter((meeting) => isMeetingOverlaping(slot, meeting));
                    if (blockMeetings.length == 1) {
                        app.history.push(['/events', event.id, 'meetings', formatURLDate(slot.start_local), blockMeetings[0].id].join('/'));
                    } else {
                        app.history.push(['/events', event.id, 'meetings', formatURLDate(slot.start_local)].join('/'));
                    }
                }

                return;
            }

            // Some mandatory session is schedule at that time
            // NOTE: yes, that attribute as a typo
            if (slot.mendatory_session) {
                const sessionView = await dialogConfirm(
                    <>
                        <p>{i18n['title.mendatory-session-slot']}</p>
                        <p>{i18n['message.mendatory-session-slot']}</p>
                    </>,
                    <TimeInterval event={event} slot={slot} />,
                    i18n['label.view-sessions'],
                    i18n['navigation.back']
                );

                if (sessionView) {
                    let session = eventSchedule.find((session) => isSessionOverlaping(session, slot));
                    if (session) {
                        app.history.push(['/events', event.id, 'schedule', formatURLDate(session.start_local), session.id].join('/'));
                    }
                }
                return;
            }

            // Other users aren't available
            if (!slot.others_available) {
                await dialogAlert(i18n['msg.unavailable-slot'], <TimeInterval event={event} slot={slot} />);
                return;
            }

            // User is busy with something else
            if (slot.self_busy && slot.type != 'meeting') {
                let title = i18n['title.session-slot'],
                    message = i18n['message.session-slot'],
                    goview = false;

                let answer = await dialogMultipleOptions(
                    <>
                        <p>{i18n['title.others-busy']}</p>
                        <p>{message}</p>
                    </>,
                    <TimeInterval event={event} slot={slot} />,
                    [i18n['label.view-sessions'], i18n['label.plan-meeting'], i18n['navigation.back']]
                );

                if (answer == i18n['label.view-sessions']) {
                    goview = true;
                } else if (answer == i18n['label.plan-meeting']) {
                    setSlot(slot);
                }

                if (goview) {
                    const session = eventSchedule.filter((session) => isSessionOverlaping(session, slot))[0];
                    if (session) {
                        app.history.push(['/events', event.id, 'schedule', formatURLDate(session.start_local), session.id].join('/'));
                    }
                }

                return;
            }

            // Other users are busy
            if (slot.others_busy) {
                const confirmed = await dialogConfirm(
                    <>
                        <p>{i18n['title.others-busy']}</p>
                        <p>{i18n['message.others-busy']}</p>
                    </>,
                    <TimeInterval event={event} slot={slot} />,
                    i18n['label.plan-meeting'],
                    i18n['navigation.back']
                );

                if (confirmed) {
                    setSlot(slot);
                }

                return;
            }

            // No other issues, select the slot
            setSlot(slot);
        },
        [event, setSlot, eventSchedule]
    );
}
